import React, { useMemo, useState } from 'react'
import { Link } from '@inertiajs/react'
import { Eye, Pencil, Users, UserCheck, UserPlus, Plus, AlertTriangle, MessageSquare } from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'
import DataTable from '../components/DataTable'
import PatientFormModal from '../components/PatientFormModal'
import { useLanguage } from '../lib/LanguageContext'

// Phase 9.6 sub-area #3 — Patients list rebuilt on the shared DataTable.

export default function Patients({ patients = [], leads = [], stats, schemes = [] }) {
  const { t, language } = useLanguage()
  const dateFmt = language === 'af' ? 'af-ZA' : 'en-ZA'

  const [modalMode, setModalMode] = useState(null)
  const [selected, setSelected]   = useState(null)
  const [showReviewOnly, setShowReviewOnly] = useState(false)
  const [showLeadsOnly, setShowLeadsOnly]   = useState(false) // WhatsApp/phone enquiries with no name+booking

  const displayPatients = showLeadsOnly
    ? leads
    : showReviewOnly
    ? patients.filter((p) => p.needs_review)
    : patients

  const openCreate = () => { setSelected(null); setModalMode('create') }
  // Medical history + notes are no longer shipped with every roster row (that was 62% of the payload and
  // made the page slow to open — Paul 2026-08-17). Open the edit modal INSTANTLY with the row we have,
  // then fetch this ONE patient's history/notes on demand and merge them in when they arrive. Non-blocking;
  // a failed fetch just leaves the medical section empty (reception can still edit the rest).
  const openEdit = (p) => {
    setSelected(p); setModalMode('edit')
    fetch(`/patients/${p.id}/medical_history`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (data) setSelected((cur) => (cur && cur.id === p.id ? { ...cur, medical_history: data.medical_history, notes: data.notes } : cur)) })
      .catch(() => {})
  }
  const closeModal = () => { setModalMode(null); setSelected(null) }

  const STATUS_OPTIONS = [
    { value: 'active',   label: t('pat_status_active') },
    { value: 'inactive', label: t('pat_status_inactive') },
  ]

  const columns = useMemo(() => [
    {
      accessorKey: 'code',
      header: t('pat_col_id'),
      cell: ({ getValue }) => (
        <span className="text-sm font-medium text-gray-500">{getValue()}</span>
      ),
    },
    {
      accessorKey: 'full_name',
      header: t('pat_col_name'),
      cell: ({ row }) => (
        <div className="min-w-0">
          <Link
            href={`/patients/${row.original.id}`}
            className="truncate text-sm font-medium text-brand-ink hover:text-brand-primary hover:underline"
          >
            {row.original.full_name}
          </Link>
          {row.original.email && (
            <p className="truncate text-xs text-brand-muted">{row.original.email}</p>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'phone',
      header: t('pat_col_phone'),
      cell: ({ getValue }) => (
        <span className="text-sm text-brand-muted">{getValue() || '—'}</span>
      ),
    },
    {
      accessorKey: 'age',
      header: t('pat_col_age'),
      cell: ({ getValue }) => (
        <span className="text-sm text-brand-muted">{getValue() ?? '—'}</span>
      ),
    },
    {
      accessorKey: 'next_appointment',
      header: t('pat_col_next'),
      sortingFn: (a, b) => {
        const av = a.original.next_appointment
        const bv = b.original.next_appointment
        if (!av && !bv) return 0
        if (!av) return 1
        if (!bv) return -1
        return new Date(av) - new Date(bv)
      },
      cell: ({ getValue }) => {
        const v = getValue()
        if (!v) return <span className="text-sm text-brand-muted/55">—</span>
        return (
          <span className="text-sm text-brand-ink">
            {new Date(v).toLocaleDateString(dateFmt, {
              year: 'numeric', month: 'short', day: 'numeric',
            })}
          </span>
        )
      },
    },
    {
      accessorKey: 'balance_cents',
      header: 'Balance',
      cell: ({ getValue }) => {
        const c = getValue()
        if (c == null) return <span className="text-sm text-brand-muted/55">—</span>
        const r = c / 100
        const fmt = (n) => `R${Math.abs(n).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        if (Math.abs(r) < 0.005) return <span className="text-sm text-brand-muted">R0.00</span>
        return r > 0
          ? <span className="text-sm font-semibold text-brand-danger">{fmt(r)} due</span>
          : <span className="text-sm font-semibold text-emerald-600">{fmt(r)} cr</span>
      },
    },
    {
      id: 'actions',
      header: t('pat_col_action'),
      enableSorting: false,
      enableGlobalFilter: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Link
            href={`/patients/${row.original.id}`}
            title={t('view')}
            aria-label={t('view')}
            className="inline-flex rounded-xl p-1.5 text-brand-muted transition-colors hover:bg-brand-surface/45 hover:text-brand-ink"
          >
            <Eye size={15} />
          </Link>
          <button
            type="button"
            title={t('edit_patient')}
            aria-label={t('edit_patient')}
            onClick={() => openEdit(row.original)}
            className="inline-flex rounded-xl p-1.5 text-brand-muted transition-colors hover:bg-brand-surface/45 hover:text-brand-ink"
          >
            <Pencil size={15} />
          </button>
        </div>
      ),
    },
  ], [t, dateFmt])

  return (
    <DashboardLayout>
      <div className="mb-8 flex items-start justify-between">
        <div>
          <span className="inline-flex items-center rounded-full border border-brand-accent bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-primary">
            {t('pat_badge')}
          </span>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-brand-ink">{t('pat_title')}</h1>
          <p className="mt-2 text-sm leading-6 text-brand-muted">
            {stats?.total ?? 0} {t('pat_registered')}
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-1.5 rounded-xl bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-primary-dark"
        >
          <Plus size={15} /> {t('pat_new')}
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          label={t('pat_total')}
          value={stats?.total ?? 0}
          icon={Users}
          color="text-brand-primary"
          tone="bg-brand-surface"
        />
        <StatCard
          label={t('pat_active')}
          value={stats?.active ?? 0}
          icon={UserCheck}
          color="text-emerald-600"
          tone="bg-brand-success/10"
        />
        <StatCard
          label={t('pat_new_month')}
          value={stats?.new_this_month ?? 0}
          icon={UserPlus}
          color="text-brand-secondary"
          tone="bg-brand-primary/10"
        />
        <button
          type="button"
          onClick={() => { setShowReviewOnly((v) => !v); setShowLeadsOnly(false) }}
          className="text-left"
        >
          <StatCard
            label={t('pat_needs_review')}
            value={stats?.needs_review ?? 0}
            icon={AlertTriangle}
            color={showReviewOnly ? 'text-white' : 'text-amber-600'}
            tone={showReviewOnly ? 'bg-amber-500' : 'bg-amber-50'}
            active={showReviewOnly}
          />
        </button>
        <button
          type="button"
          onClick={() => { setShowLeadsOnly((v) => !v); setShowReviewOnly(false) }}
          className="text-left"
          title="WhatsApp/phone enquiries with no name or booking yet — kept here, off the main roster"
        >
          <StatCard
            label="Leads / enquiries"
            value={stats?.leads ?? 0}
            icon={MessageSquare}
            color={showLeadsOnly ? 'text-white' : 'text-sky-600'}
            tone={showLeadsOnly ? 'bg-sky-500' : 'bg-sky-50'}
            active={showLeadsOnly}
          />
        </button>
      </div>

      <DataTable
        columns={columns}
        data={displayPatients}
        globalFilterPlaceholder={t('pat_search')}
        initialSort={[{ id: 'code', desc: false }]}
        pageSize={100}
        totalLabel={t('pat_total_label')}
        emptyMessage={t('pat_empty')}
      />

      <PatientFormModal
        open={modalMode === 'create'}
        mode="create"
        schemes={schemes}
        onClose={closeModal}
      />
      <PatientFormModal
        open={modalMode === 'edit'}
        mode="edit"
        patient={selected}
        medicalHistory={selected?.medical_history}
        bloodTypes={selected?.medical_history?.blood_types}
        schemes={schemes}
        onClose={closeModal}
      />
    </DashboardLayout>
  )
}

function StatCard({ label, value, icon: Icon, color, tone = 'bg-brand-surface', active = false }) {
  return (
    <div className={`flex items-center gap-4 rounded-xl border p-4 shadow-sm ${
      active ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-200' : 'border-brand-border bg-white'
    }`}>
      <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl ${tone}`}>
        <Icon size={18} className={color} />
      </div>
      <div>
        <p className={`text-2xl font-bold ${active ? 'text-amber-700' : color}`}>{value}</p>
        <p className="text-xs uppercase tracking-wide text-brand-muted">{label}</p>
      </div>
    </div>
  )
}

function StatusBadge({ status, t }) {
  const styles = status === 'active'
    ? 'border border-brand-success/15 bg-brand-success/10 text-brand-success'
    : 'border border-brand-muted/15 bg-brand-surface text-brand-muted'
  const label = status === 'active' ? t('pat_status_active') : t('pat_status_inactive')
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${styles}`}>
      {label}
    </span>
  )
}

